/**
 * The HTTP client.
 *
 * Two responsibilities beyond calling `fetch`, and both exist because getting
 * them wrong is expensive rather than annoying:
 *
 *  1. **Attach `initData` to every request.** §7.1 verifies it server-side on
 *     each call, so there is no session to forget to refresh — but equally, a
 *     request that omits it is simply a 401. Centralising it means no screen
 *     can forget.
 *  2. **Attach `Idempotency-Key` to every write that moves money.** A phone on
 *     a bad connection retries, and a player who taps "join" twice because the
 *     first tap seemed not to register is the normal case, not the edge case.
 *     The key is generated per *intent*, so a retry of the same intent carries
 *     the same key and the server replays its first answer (§7.7).
 */

import { getInitData } from '../telegram.ts';
import type {
  ApiErrorBody,
  BalanceView,
  BankMethod,
  CardView,
  DepositView,
  PaymentDestinationView,
  PublicConfigView,
  RoundHistoryView,
  RoundView,
  SessionResponse,
  WalletTransactionView,
  WithdrawalView,
} from './types.ts';

const BASE = (import.meta.env['VITE_API_BASE'] as string | undefined) ?? '/api';

/**
 * A failed request, carrying the server's stable error code.
 *
 * Screens branch on `code` — `insufficient_funds` and `round_full` need
 * different words and different recovery — and never on the message, which is
 * prose and will change.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    super(body?.error?.message ?? fallback);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error?.code ?? 'network_error';
    this.details = body?.error?.details;
  }

  /** True when retrying the identical request could plausibly succeed. */
  get retryable(): boolean {
    return this.status === 0 || this.status >= 500 || this.code === 'request_in_progress';
  }
}

/**
 * Read once, from the URL the mini app was opened with, and remembered for
 * the session.
 *
 * Telegram preserves the start URL across in-app navigation, but the app
 * rewrites its own query string as the player moves around, so reading it
 * lazily on the tenth request would find it gone. `sessionStorage` rather
 * than `localStorage`: which bot opened this app is a property of this
 * session, not of the device — the same phone can hold two agents' bots.
 */
let cachedBotSlug: string | null | undefined;
function getAgentBotSlug(): string | null {
  if (cachedBotSlug !== undefined) return cachedBotSlug;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('a');
    const stored = window.sessionStorage.getItem('agentBot');
    const slug = fromUrl ?? stored;
    cachedBotSlug = slug && /^[a-z0-9][a-z0-9-]{1,30}$/.test(slug) ? slug : null;
    if (cachedBotSlug) window.sessionStorage.setItem('agentBot', cachedBotSlug);
  } catch {
    // A blocked sessionStorage must not take the whole client down; the app
    // still works on the house bot, which is the no-slug case anyway.
    cachedBotSlug = null;
  }
  return cachedBotSlug;
}

interface RequestOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
  /** Sent as `Idempotency-Key`. Required by convention on money writes. */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const initData = getInitData();
  if (!initData) {
    // Distinct from a 401: there is nothing to retry and no server involved.
    // The app shows "open this from Telegram" rather than "login failed".
    throw new ApiError(0, null, 'not running inside Telegram');
  }

  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {
    // Telegram's own convention for Mini App requests.
    authorization: `tma ${initData}`,
  };
  // Which agent's bot this copy of the app was opened from, if any — an
  // agent's mini app link is `…/?a=<slug>` (migration 071). The server needs
  // it to know which bot token to verify `initData` against; it grants
  // nothing on its own, since naming the wrong bot only makes the signature
  // check fail.
  const botSlug = getAgentBotSlug();
  if (botSlug) headers['x-agent-bot'] = botSlug;
  // `FormData` sets its own `Content-Type`, including the multipart boundary the
  // server needs to parse the body. Setting one here would overwrite it with a
  // boundary-less header and every upload would fail as unparseable.
  const isForm = options.body instanceof FormData;
  // Sent even for a bodyless POST (e.g. `session()`), not just when there's a
  // body to describe — a proxy in front of the API (observed: a Cloudflare
  // Tunnel's HTTP/2-to-HTTP/1.1 translation) can turn a content-type-less
  // POST into one the server rejects with 415, and an explicit header here
  // sidesteps whatever that layer does rather than depending on it.
  if (!isForm && method !== 'GET') headers['content-type'] = 'application/json';
  if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey;

  let response: Response;
  try {
    response = await fetch(BASE + path, {
      method,
      headers,
      ...(options.body === undefined
        ? {}
        : { body: isForm ? (options.body as FormData) : JSON.stringify(options.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    // Status 0 means the request never reached the server — safe to retry even
    // for a write, since nothing can have happened.
    throw new ApiError(0, null, 'network unavailable');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }

  if (!response.ok) {
    throw new ApiError(response.status, parsed as ApiErrorBody | null, response.statusText);
  }
  return parsed as T;
}

/**
 * A key for one *intent*, stable across retries of that intent.
 *
 * Deliberately not random per call: a fresh key on every attempt would make
 * each retry a new request to the server and defeat the whole mechanism. The
 * caller decides what constitutes one intent — one tap of one button.
 */
export function intentKey(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

/**
 * The unauthenticated config surface — currently just the support contact.
 *
 * Cannot go through `request()`: that refuses to run outside Telegram, and the
 * whole point of this call is to be reachable when authentication *fails* (a
 * suspended account can't open a session but most needs a way to reach a
 * human). Failure here is not an error state either — no support URL configured
 * or a flaky first request simply means the affordances stay hidden.
 */
export async function publicConfig(): Promise<PublicConfigView> {
  try {
    const response = await fetch(BASE + '/config/public');
    if (!response.ok) return { supportUrl: null };
    return (await response.json()) as PublicConfigView;
  } catch {
    return { supportUrl: null };
  }
}

export const api = {
  /** Verifies initData and registers the player on first sight. */
  session: (): Promise<SessionResponse> => request('/auth/session', { method: 'POST' }),

  me: (): Promise<SessionResponse> => request('/me'),

  balance: (): Promise<BalanceView> => request('/me/balance'),

  rounds: (): Promise<{ rounds: RoundView[] }> => request('/rounds'),

  /** Past rounds the player joined — the "Past Rounds" screen. */
  roundHistory: (): Promise<{ rounds: RoundHistoryView[] }> => request('/rounds/history'),

  round: (id: string, signal?: AbortSignal): Promise<{ round: RoundView; calledNumbers: number[] }> =>
    request(`/rounds/${id}`, { ...(signal ? { signal } : {}) }),

  myCards: (roundId: string): Promise<{ cards: CardView[] }> =>
    request(`/rounds/${roundId}/cards`),

  /**
   * Buys one cartela — the number the player tapped on the picker, or
   * omitted for the old auto-assign behaviour. The key is required, not
   * optional: this is the write where a double-tap is least recoverable —
   * two cartelas for one player is a legitimate purchase when
   * `maxCardsPerUser` allows it, so no constraint underneath can tell an
   * accident from an intent. Mint a fresh key per *number* attempted — a
   * retry of the same number replays safely, but trying a different number
   * after the first was taken is a new intent, not the same one retried.
   */
  buyCartela: (
    roundId: string,
    key: string,
    cartelaNumber?: number,
  ): Promise<{ roundId: string; card: CardView }> =>
    request(`/rounds/${roundId}/cartelas`, {
      method: 'POST',
      idempotencyKey: key,
      ...(cartelaNumber === undefined ? {} : { body: { cartelaNumber } }),
    }),

  /** Which picker numbers are already taken — the picker grid's initial paint. */
  takenCartelas: (roundId: string): Promise<{ taken: number[] }> =>
    request(`/rounds/${roundId}/cartelas/taken`),

  /**
   * "I'm about to pick a cartela" — call this the instant the picker opens,
   * before the player has bought anything, so phantom test accounts (§4,
   * local/testing only) start trickling in immediately instead of only
   * after the purchase completes. Fire-and-forget by design: nothing about
   * the picker should wait on this.
   */
  cartelaIntent: (roundId: string): Promise<{ ok: boolean }> =>
    request(`/rounds/${roundId}/cartelas/intent`, { method: 'POST' }),

  /**
   * Backs out of a cartela within the grace window for an instant refund.
   * No idempotency key: it's a plain delete-and-refund, and a retry after
   * success just fails with "cartela not found", which is the right answer.
   */
  leaveCartela: (roundId: string, cardId: string): Promise<{ ok: boolean }> =>
    request(`/rounds/${roundId}/cartelas/${cardId}/leave`, { method: 'POST' }),

  // --- wallet --------------------------------------------------------------

  depositMethods: (): Promise<{ methods: PaymentDestinationView[] }> =>
    request('/deposit-methods'),

  deposits: (): Promise<{ deposits: DepositView[] }> => request('/deposits'),

  /**
   * Claims a transfer. Moves no money — an admin approving it is what credits
   * the player (§0.5), so this is a form submission, not a payment.
   *
   * Keyed anyway: `UNIQUE(method, tx_reference)` would reject a double-tap, but
   * with a 409 that reads to the player as "you already claimed this" when what
   * actually happened is their first tap succeeded and they never saw it.
   */
  submitDeposit: (
    body: { method: BankMethod; amount: string; txReference: string },
    key: string,
  ): Promise<DepositView> =>
    request('/deposits', { method: 'POST', body, idempotencyKey: key }),

  withdrawals: (): Promise<{ withdrawals: WithdrawalView[] }> => request('/withdrawals'),

  /**
   * The wallet timeline from the ledger itself — every posting on the
   * available balance, including ones with no workflow row (referral bonus,
   * win, refund). This is what makes a balance change explicable in-app.
   */
  transactions: (): Promise<{ transactions: WalletTransactionView[] }> =>
    request('/me/transactions'),

  withdrawableBalance: (): Promise<BalanceView> => request('/withdrawals/balance'),

  /**
   * Requests a withdrawal. Unlike a deposit this *does* move money immediately:
   * the funds are locked the instant the row exists, so they stop being
   * spendable before anyone reviews it.
   */
  requestWithdrawal: (
    body: { method: BankMethod; amount: string; destinationDetail: string },
    key: string,
  ): Promise<WithdrawalView> =>
    request('/withdrawals', { method: 'POST', body, idempotencyKey: key }),

  /** Player accepts Terms & Conditions. */
  acceptTerms: (): Promise<{ ok: boolean }> =>
    request('/me/accept-terms', { method: 'POST' }),

  /** Player consents to phone/username data collection. */
  giveConsent: (): Promise<{ ok: boolean }> =>
    request('/me/give-consent', { method: 'POST' }),

  /**
   * Syncs the locale the bot renders this player's notifications in —
   * called whenever the app's own locale resolves (on load and on an
   * explicit switch), never a separate setting the player picks twice.
   */
  setLanguage: (language: string): Promise<{ ok: boolean }> =>
    request('/me/language', { method: 'POST', body: { language } }),
};
